import { Params } from '@angular/router';

import { HttpHeaders } from '@angular/common/http';
import { KeyOf } from '@app/core/utilities/typescript.utilities';
import { Observable } from 'rxjs';

export const API_DEFAULT_OFFSET = 0;

export const API_DEFAULT_LIMIT = 10;

export const API_MAX_LIMIT = 10000;

export const API_DEFAULT_SEARCH_DEBOUNCE = 200;

export enum ApiEndpoint {
  Auth = 'auth',
  Signup = 'signup',
  Accounts = 'accounts',
  Notifications = 'notifications',
  Customers = 'customers',
  Preview = 'preview',
  Invoices = 'invoices',
  Users = 'users',
  Changelog = 'changelog',
  Reports = 'reports',
  EntryTypes = 'entry-types',
  CurrencyRates = 'currency-rates',
  Portal = 'portal',
  RAS = 'ras',
}

export enum ApiCommonRoutes {
  Session = 'session',
  Summary = 'summary',
  Tags = 'tags',
  Performance = 'performance',
  Scenarios = 'scenarios',
  PerformanceGroups = 'performance-groups',
  PerformanceGroupsSuggestions = 'performance-groups-suggestions',
  PerformanceGroupsExtended = 'performance-groups-extended',
  SnapshotPerformance = 'snapshot-performance',
  Campaigns = 'campaigns',
  List = 'list',
  Management = 'management',
  Retailers = 'retailers',
  Portfolios = 'portfolios',
  Labels = 'labels',
  Accounts = 'accounts',
  Log = 'log',
  Bidding = 'bidding',
  BidAdjustments = 'bid-adjustments',
  PlatformSettings = 'platform-settings',
  CriterionAddition = 'criterion-addition',
  Users = 'users',
  EntryTypes = 'entry-types',
  AdGroups = 'adgroups',
  AdGroup = 'adgroup',
  Criteria = 'criteria',
  Rules = 'rules',
  Suggestions = 'proposals',
  AdsSync = 'sync',
  RuleTypes = 'rule-types',
  CreationQueue = 'creation-queue',
  Amazon = 'amazon',
  SponsoredProducts = 'sp',
  Utils = 'utils',
  BidMetrics = 'bid_metrics',
  BidSuggestions = 'bid-suggestions',
}

export type ApiMethod = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

export type CustomerPerformanceGroupBy = 'ACCOUNT' | 'AMZ-PORTFOLIO' | 'CAMPAIGN' | 'DATE' | 'PERFORMANCE-GROUP';

export type AccountPerformanceGroupBy = 'ADGROUP' | 'CAMPAIGN' | 'CRITERION' | 'DATE';

export type ApiGlobalFilterProperties = ApiRequestValues<{ customerId: number }>;

export enum ApiAccessLevel {
  None = 'none',
  User = 'user',
  CustomerAdmin = 'customer-admin',
  AgencyAdmin = 'agency-admin',
  SysAdmin = 'sys-admin',
}

export enum CustomerApiRoute {
  BillingDetails = 'billing-details',
  PaymentMethods = 'payment-methods',
  Subscription = 'subscription',
  Plan = 'plan',
  PlanConstraints = 'plan-constraints',
  SubscriptionConstraints = 'subscription-constraints',
  AccountTrials = 'account-trials',
  AccountTrialsConstraints = 'account-trials-constraints',
  Access = 'access',
  Campaigns = 'campaigns',
  RetailAccounts = 'retail-accounts',
  CriterionAddition = 'criterion-addition',
  ProductAds = 'product-ax',
  Budget = 'budget',
  OptimizationSettings = 'opt-settings',
  AdGroups = 'adgroups',
  PerformanceGroups = 'performance-groups',
  BudgetSuggestions = 'budget-suggestions',
  Performance = 'performance',
  Summary = 'summary',
  ThirdPartyAccounts = 'third-party-accounts',
  SuggestionsFeedback = 'suggestions-feedback',
  ManagementEdit = 'management/edit',
}

export enum AccountApiRoute {
  Move = 'move',
  Insights = 'insights',
}

export enum UserApiRoute {
  Access = 'access',
  ApolloSettings = 'apollo-settings',
  CurrencyRates = 'currency-rates',
  EmailVerification = 'email-verification',
}

export enum ChangelogApiRoute {
  ObjectTypes = 'object-types',
}

export enum AuthApiRoute {
  Keys = 'keys',
  InvalidateTokens = 'invalidate-tokens',
}

export enum CriterionAdditionApiRoute {
  Rules = 'rules',
  Settings = 'settings',
  RuleTypes = 'rule-types',
  DestinationAdgroups = 'destination-adgroups',
  DestinationTypes = 'destination-types',
  Proposals = 'proposals',
  DestinationState = 'state',
}

export enum ProductAdsApiRoute {
  SyncTargets = 'sync-targets',
  Campaign = 'campaign',
  AdGroup = 'adgroup',
}

export enum InsightsApiRoute {
  Campaigns = 'campaigns',
  AdGroups = 'adgroups',
}

export enum AlertsApiRoute {
  Categories = 'categories',
  Definitions = 'definitions',
  DigestInterval = 'digest-intervals',
  Instances = 'instances',
  Muting = 'muting',
}

export enum ManagementApiRoute {
  Create = 'create',
  Queue = 'queue',
  Edit = 'edit',
}

export enum ManagementLevel {
  AdGroup = 'ADGROUP',
  Campaign = 'CAMPAIGN',
  Criterion = 'CRITERION',
  Product = 'PRODUCT',
}

export interface ApiMutatorSettings {
  searchDebounceTime?: number;
  searchParam?: string;
  persistent?: boolean;
  propagateError?: boolean;
  accessLevel?: ApiAccessLevel;
  impersonationId?: number;
}

export interface ApiListMutatorSettings<T extends object> extends ApiMutatorSettings {
  trackById?: KeyOf<T>;
  disablePaging?: boolean;
  listMutator?: (data: T[]) => T[] | Observable<T[]>;
}

// @todo when upgraded to TypeScript 4.1. Will enable deep object reference through string
// export type ApiResponseFieldReference<T> = DottedObjectPath<T> | DottedObjectPath<T>[];
export type ApiResponseFieldReference<T extends object> = KeyOf<T>[] | KeyOf<T>;

export type ApiRequestValues<
  T extends object,
  U = (string | number | boolean) | (string | number | boolean)[],
> = Partial<Record<KeyOf<T>, U> & Record<`${KeyOf<T>}Set`, U>>;

export type ApiRoute = (string | number | ApiCommonRoutes)[] | string | number | ApiCommonRoutes;

export type ApiFilterValues<T extends object = object> = ApiGetRequest<T>['filterValues'];
export type ApiSearchValues<T extends object = object> = ApiGetRequest<T>['searchValues'];

export enum ApiRequestHeader {
  AccessLevel = 'X-Adspert-Access-Level',
  Impersonate = 'X-Adspert-Impersonate',
  TableColumns = 'X-Adspert-Table-Columns',
  Humanize = 'X-Adspert-CSV-Humanize',
  XSRF = 'X-XSRF-TOKEN',
}

export enum GroupByValues {
  DATE = 'date',
  CAMPAIGN = 'campaign',
  PG = 'PERFORMANCE-GROUP',
}

export enum Initiator {
  Assistant = 'ASSISTANT',
  User = 'USER',
}

export interface ApiRequest {
  method?: ApiMethod;
  endpoint: ApiEndpoint;
  route?: ApiRoute;
  params?: Params;
  accessLevel?: ApiAccessLevel;
  impersonationId?: number;
  observe?: 'body' | 'response' | 'events';
}

export interface ApiGetRequest<T extends object> extends ApiRequest {
  method?: 'GET';

  include?: ApiResponseFieldReference<T>;
  exclude?: ApiResponseFieldReference<T>;

  searchTerm?: string | number;
  searchFields?: ApiResponseFieldReference<T>;

  sortValues?: ApiRequestValues<T, number>;
  searchValues?: ApiRequestValues<T>;
  filterValues?: ApiRequestValues<T> & ApiGlobalFilterProperties;
  fixedFilterValues?: ApiRequestValues<T> & ApiGlobalFilterProperties;

  snakeCase?: boolean;
  groupBy?: CustomerPerformanceGroupBy | AccountPerformanceGroupBy | KeyOf<T>;
}

// eslint-disable-next-line
export interface ApiGetObjectRequest<T extends object> extends ApiGetRequest<T> {}

export interface ApiGetListRequest<T extends object> extends ApiGetRequest<T> {
  limit?: typeof API_DEFAULT_LIMIT | number;
  offset?: typeof API_DEFAULT_OFFSET | number;

  disablePaging?: boolean;
}

export interface ApiDataRequest<T extends object> extends ApiRequest {
  data: Partial<T>;
}

export interface ApiPostRequest<T extends object> extends ApiDataRequest<T> {
  method?: 'POST';
  data: Partial<T>;
}

export interface ApiPatchRequest<T extends object> extends ApiDataRequest<T> {
  method?: 'PATCH';
  data: Partial<T>;
}

export interface ApiPutRequest<T extends object> extends ApiDataRequest<T> {
  method?: 'PUT';
  data: Partial<T>;
}

export interface ApiDeleteRequest<T extends object> extends ApiDataRequest<T> {
  method?: 'DELETE';
  data: Partial<T>;
}

export interface ApiResponse {
  status: number;
  message: string;
  headers?: HttpHeaders;
}

export interface ApiPaginationOptions {
  offset: number;
  limit: number;
  total: number;
}

export interface ApiPaginationResponse<T extends object> extends ApiPaginationOptions {
  sort?: ApiRequestValues<T, number>;
}

export interface ApiMetaResponse<T extends object> {
  pagination?: ApiPaginationResponse<T>;
}

export interface ApiGetObjectResponse<T extends object> extends ApiResponse {
  data: T | null;
}

export interface ApiGetListResponse<T extends object> extends ApiResponse {
  data: T[];
  meta: ApiMetaResponse<T>;
}

export interface ApiPostResponse<T extends object> extends ApiResponse {
  data?: T | null;
}

// eslint-disable-next-line
export interface ApiPatchResponse<T extends object = object> extends ApiResponse {
  data?: T | null;
}

// eslint-disable-next-line
export interface ApiPutResponse<T extends object = object> extends ApiResponse {
  data?: T | null;
}

// eslint-disable-next-line
export interface ApiDeleteResponse extends ApiResponse {}

export interface ApiErrorResponsePath {
  path: (string | number)[];
  message: string;
}

export interface ApiErrorResponse extends ApiResponse {
  errors?: ApiErrorResponsePath | ApiErrorResponsePath[];
}
